import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const INITIAL_STATE = {
  bill_number: '', total_bill_amount: '', amount_paid: '',
  customer_name: '', customer_contact: '', customer_email: '',
  payment_method: 'cash',
  gst_type: 'without_gst', gst_percent: '5', custom_gst: '',
};

export const SaleEntryForm = ({ selectedDate, onEntryAdded }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [pendingBills, setPendingBills] = useState([]);
  const [selectedPending, setSelectedPending] = useState('');

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/sales/pending-list`);
        setPendingBills(res.data);
      } catch (err) { toast.error("Failed to load pending bills"); }
    };
    fetchPending();
  }, []);

  const baseAmt = parseFloat(form.total_bill_amount) || 0;
  const gstPct = form.gst_type === 'with_gst' ? (parseFloat(form.gst_percent === 'custom' ? form.custom_gst : form.gst_percent) || 0) : 0;
  const gstAmt = +(baseAmt * gstPct / 100).toFixed(2);
  const grandTotal = +(baseAmt + gstAmt).toFixed(2);
  const amountPaid = parseFloat(form.amount_paid) || 0;
  const balance = Math.max(grandTotal - amountPaid, 0);
  const getPaymentStatus = () => {
    if (amountPaid <= 0) return 'Pending';
    if (amountPaid >= grandTotal) return 'Paid';
    return 'Partial';
  };
  const status = getPaymentStatus();

  const handlePendingSelect = (billNumber) => {
    setSelectedPending(billNumber);
    if (!billNumber) return;
    const bill = pendingBills.find(b => b.bill_number === billNumber);
    if (bill) {
      setForm(prev => ({
        ...prev,
        bill_number: bill.bill_number,
        total_bill_amount: String(bill.total_bill_amount || bill.amount || 0),
        amount_paid: '',
        customer_name: bill.customer_name || '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bill_number || !form.total_bill_amount) {
      toast.error('Bill number and total bill amount are required');
      return;
    }

    // If paying against a pending bill, update it instead
    const pendingBill = pendingBills.find(b => b.bill_number === form.bill_number);
    if (pendingBill && pendingBill.sale_id && amountPaid > 0) {
      try {
        setLoading(true);
        const newPaid = (pendingBill.amount_paid || 0) + amountPaid;
        await axios.put(`${API}/sales/${pendingBill.sale_id}`, {
          date: pendingBill.date,
          bill_number: pendingBill.bill_number,
          total_bill_amount: pendingBill.total_bill_amount || pendingBill.amount,
          amount_paid: newPaid,
          customer_name: pendingBill.customer_name || form.customer_name,
          customer_contact: form.customer_contact,
          customer_email: form.customer_email,
          payment_method: form.payment_method,
        });
        toast.success(`Payment of ₹${amountPaid} applied to ${form.bill_number}!`);
        setForm(INITIAL_STATE);
        setSelectedPending('');
        onEntryAdded();
        return;
      } catch (err) {
        toast.error('Failed to apply payment');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/sales`, {
        date: selectedDate,
        bill_number: form.bill_number,
        total_bill_amount: baseAmt,  // backend treats as base when gst_type=with_gst
        base_amount: baseAmt,
        gst_type: form.gst_type,
        gst_percent: gstPct,
        amount_paid: amountPaid,
        customer_name: form.customer_name,
        customer_contact: form.customer_contact,
        customer_email: form.customer_email,
        payment_method: form.payment_method,
      });
      const getSuccessMsg = () => {
        if (status === 'Paid') return 'Sale added - Fully Paid!';
        if (status === 'Partial') return 'Sale added - Partial Payment!';
        return 'Sale added - Pending Payment!';
      };
      toast.success(getSuccessMsg());
      setForm(INITIAL_STATE);
      setSelectedPending('');
      onEntryAdded();
    } catch (err) {
      toast.error('Failed to add sale');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(v);

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="sale-entry-form">
      {/* Quick Balance Payment */}
      {pendingBills.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded space-y-2">
          <Label className="text-sm font-semibold text-amber-900">Quick Balance Payment</Label>
          <Select value={selectedPending} onValueChange={handlePendingSelect}>
            <SelectTrigger className="bg-white" data-testid="pending-bill-select"><SelectValue placeholder="Select pending bill..." /></SelectTrigger>
            <SelectContent>
              {pendingBills.map(b => (
                <SelectItem key={b.bill_number} value={b.bill_number}>
                  {b.bill_number} - {b.customer_name || 'N/A'} - Bal: {formatCurrency(b.balance_amount || (b.amount - (b.amount_paid || 0)))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-sm font-semibold">Bill Number *</Label>
        <Input value={form.bill_number} onChange={(e) => update('bill_number', e.target.value)} placeholder="INV-001" required data-testid="sale-bill-number" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Total Bill Amount (₹) *</Label>
        <Input type="number" step="0.01" value={form.total_bill_amount} onChange={(e) => update('total_bill_amount', e.target.value)} className="currency" placeholder="Base amount (pre-GST if GST bill)" required data-testid="sale-total-bill" />
      </div>

      {/* GST Section */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
        <Label className="text-sm font-bold text-blue-700">GST Details</Label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => update('gst_type', 'without_gst')} className={`h-9 rounded text-xs font-bold border-2 transition-all ${form.gst_type === 'without_gst' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300'}`} data-testid="sale-gst-without">Not GST Bill</button>
          <button type="button" onClick={() => update('gst_type', 'with_gst')} className={`h-9 rounded text-xs font-bold border-2 transition-all ${form.gst_type === 'with_gst' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-600 border-blue-300'}`} data-testid="sale-gst-with">GST Bill</button>
        </div>
        {form.gst_type === 'with_gst' && (
          <div className="grid grid-cols-7 gap-1">
            {['0', '5', '12', '18', '28', 'custom'].map((p) => (
              <button key={p} type="button" onClick={() => update('gst_percent', p)} className={`h-8 rounded text-[11px] font-bold border ${form.gst_percent === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-600 border-blue-200'}`} data-testid={`sale-gst-pct-${p}`}>{p === 'custom' ? 'Cust' : `${p}%`}</button>
            ))}
            {form.gst_percent === 'custom' && (
              <Input type="number" min="0" max="100" placeholder="%" value={form.custom_gst} onChange={(e) => update('custom_gst', e.target.value)} className="h-8 text-[11px] col-span-1" data-testid="sale-custom-gst" />
            )}
          </div>
        )}
      </div>

      <div>
        <Label className="text-sm font-semibold">Amount Paid (₹)</Label>
        <Input type="number" step="0.01" value={form.amount_paid} onChange={(e) => update('amount_paid', e.target.value)} className="currency" placeholder="0.00" data-testid="sale-amount-paid" />
      </div>

      {/* Total / GST / Balance preview */}
      <div className="bg-gray-50 border rounded p-3 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-gray-600">Base Amount:</span><span className="font-mono font-bold text-gray-900">{formatCurrency(baseAmt)}</span></div>
        {form.gst_type === 'with_gst' && (
          <div className="flex justify-between"><span className="text-blue-700">+ GST ({gstPct}%):</span><span className="font-mono font-bold text-blue-700" data-testid="sale-gst-amount">{formatCurrency(gstAmt)}</span></div>
        )}
        <div className="flex justify-between border-t pt-1"><span className="text-gray-700 font-bold">Grand Total:</span><span className="font-mono font-black text-emerald-700" data-testid="sale-grand-total">{formatCurrency(grandTotal)}</span></div>
        <div className="flex justify-between">
          <span className="text-gray-600">Balance:</span>
          <span className={`font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="sale-balance-preview">{formatCurrency(balance)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`font-semibold ${status === 'Paid' ? 'text-green-600' : status === 'Partial' ? 'text-amber-600' : 'text-red-600'}`} data-testid="sale-status-preview">{status}</span>
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold">Payment Method</Label>
        <Select value={form.payment_method} onValueChange={(v) => update('payment_method', v)}>
          <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="bank">Bank</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm font-semibold">Customer Name</Label>
        <Input value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} placeholder="Customer name" data-testid="sale-customer-name" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-sm font-semibold text-xs">Phone</Label>
          <Input value={form.customer_contact} onChange={(e) => update('customer_contact', e.target.value)} placeholder="Phone" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-xs">Email</Label>
          <Input type="email" value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} placeholder="Email" />
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full premium-button bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700" data-testid="submit-sale-btn">
        {loading ? 'Processing...' : selectedPending ? 'Apply Payment' : 'Add Sale'}
      </Button>
    </form>
  );
};
